import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator as CalculatorIcon } from "lucide-react";
import { toast } from "sonner";
import BlueprintBackground from "@/components/BlueprintBackground";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import VisionBanner from "@/components/VisionBanner";
import Discovery from "@/components/Discovery";
import InfraTracker from "@/components/InfraTracker";
import MajorInvestments from "@/components/MajorInvestments";
import Footer from "@/components/Footer";
import Calculator from "@/components/Calculator";
import LeadModal from "@/components/LeadModal";
import CompareTray from "@/components/CompareTray";
import { PROPERTIES } from "@/data/dholera";

export default function Landing({ theme, onToggleTheme, onNavigate }) {
  const navigate = useNavigate();
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcSeed, setCalcSeed] = useState(null);
  const [lead, setLead] = useState(null);
  const [compareIds, setCompareIds] = useState([]);

  const openCalc = () => setCalcOpen(true);

  const calcFromParcel = (p) => {
    setCalcSeed({
      area: p.plotSqYd,
      pps: p.pricePerSqYd,
      name: p.name,
      ts: Date.now(),
    });
    setCalcOpen(true);
  };

  const openDetail = (p) => navigate(`/parcel/${p.id}`);

  const toggleCompare = (p) => {
    setCompareIds((ids) => {
      if (ids.includes(p.id)) return ids.filter((i) => i !== p.id);
      if (ids.length >= 3) {
        toast.info("Compare tray holds up to 3 parcels — remove one first.");
        return ids;
      }
      return [...ids, p.id];
    });
  };

  const compareItems = compareIds
    .map((cid) => PROPERTIES.find((p) => p.id === cid))
    .filter(Boolean);

  return (
    <div
      id="top"
      className="relative min-h-screen bg-background font-sans text-foreground antialiased"
    >
      <BlueprintBackground />
      <Navbar
        theme={theme}
        onToggleTheme={onToggleTheme}
        onNavigate={onNavigate}
        onOpenCalc={openCalc}
      />
      <main>
        <Hero onNavigate={onNavigate} onOpenCalc={openCalc} />
        <VisionBanner />
        <Manifesto />
        <Discovery
          onLead={(property, mode) => setLead({ property, mode })}
          onCalc={calcFromParcel}
          onOpenDetail={openDetail}
          compareIds={compareIds}
          onToggleCompare={toggleCompare}
        />
        <InfraTracker />
        <MajorInvestments />
      </main>
      <Footer onNavigate={onNavigate} />

      <Calculator open={calcOpen} onOpenChange={setCalcOpen} seed={calcSeed} />
      <LeadModal lead={lead} onClose={() => setLead(null)} />
      <CompareTray
        items={compareItems}
        onRemove={(rid) => setCompareIds((ids) => ids.filter((i) => i !== rid))}
        onClear={() => setCompareIds([])}
        onExpress={(p) => setLead({ property: p, mode: "interest" })}
      />

      <AnimatePresence>
        {!calcOpen && (
          <motion.button
            key="calc-fab"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={openCalc}
            className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-primary px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-primary-foreground shadow-2xl transition-colors duration-200 hover:bg-accent"
            data-testid="open-calculator-fab"
          >
            <CalculatorIcon className="h-4 w-4" />
            ROI Engine
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
